import type {
  PtoDate,
  ResourceAvailabilityResponse,
  ResourceAvailabilityRow,
  StatHolidayDate,
  WorkerCertificationEntry,
  WorkerMonthCell,
  WorkerSkillEntry,
  WorkerView,
} from '@/types/ui/resource-availability'
import { isCurrentMonth, isCurrentWeek, monthLabel, weekLabel, workerInitials } from '@/lib/utils/availability-utils'

export interface WorkerProfileSummary {
  userProfileId: number
  workerName: string
  workerRole?: string
  jobFunctions?: string[]
  skills?: WorkerSkillEntry[]
  certifications?: WorkerCertificationEntry[]
  branchCode?: string
  workerLocation?: string
  procurementSourceCode?: string
  functionalManagerProfileId?: number
}

/**
 * Pivot the (worker × week) row set into one entry per worker with both monthly and
 * weekly cell arrays pre-computed. The granularity toggle in the UI flips which array
 * the heatmap renders without a refetch.
 *
 * Rows are *sparse* — the BE drops weeks where every signal (planned, PTO, stat,
 * engagements) is zero/null. The set of in-scope workers comes from
 * `response.workerHoursPerDay` (which always lists every in-scope worker, including
 * fully-idle ones). Missing weeks render as empty cells with capacity =
 * hoursPerDay × actual-working-days, computed from the window bounds.
 */
export function buildWorkerViews(
  response: ResourceAvailabilityResponse,
  profiles: Map<number, WorkerProfileSummary>
): WorkerView[] {
  const orderedWeeks = weeksInWindow(response.startMonth, response.endMonth)
  const orderedMonths = monthsInWindow(response.startMonth, response.endMonth)

  // Idle-cell capacity sizing. Weekly cells cover WHOLE weeks, so an idle week always sizes to
  // a full 5 weekdays (real rows carry the BE's own count). Monthly idle cells size to the
  // weekdays of that month within the window. Cached once per response (worker-independent).
  const windowEndIso = lastDayOfMonthIso(response.endMonth)
  const workingDaysByMonth = new Map<string, number>()
  for (const monthStartIso of orderedMonths) {
    workingDaysByMonth.set(monthStartIso, countWeekdaysInMonth(monthStartIso, response.startMonth, windowEndIso))
  }

  const byWorker = new Map<number, ResourceAvailabilityRow[]>()
  for (const row of response.rows) {
    const list = byWorker.get(row.userProfileId) ?? []
    list.push(row)
    byWorker.set(row.userProfileId, list)
  }

  // Monthly buckets come from the BE as their own row set (correctly clamped to each month's
  // in-window days). Group them per worker; fall back to weekly aggregation only when an older
  // BE omits them.
  const byWorkerMonthly = new Map<number, ResourceAvailabilityRow[]>()
  for (const row of response.monthlyRows ?? []) {
    const list = byWorkerMonthly.get(row.userProfileId) ?? []
    list.push(row)
    byWorkerMonthly.set(row.userProfileId, list)
  }
  // Presence, not length: the new BE always sets monthlyRows (possibly empty when all data is
  // in out-of-window edge days or the window is idle); only an older BE omits the field. Using
  // length would misread a legitimately-empty new-BE payload as "old BE" and fall back to
  // weekly aggregation, leaking a leading week's out-of-window days into the monthly view.
  const hasMonthlyRows = response.monthlyRows !== undefined

  // The new BE includes `workerHoursPerDay` covering every in-scope worker (so fully-idle
  // workers under sparse rows still surface). Fall back to deriving the worker set from
  // the rows themselves when the field is missing or empty — this happens against an
  // older BE that hasn't been redeployed, and prevents the heatmap from going blank in
  // that case.
  const workerHoursPerDayMap: Record<string, number> = {}
  const sourceMap = response.workerHoursPerDay
  if (sourceMap && Object.keys(sourceMap).length > 0) {
    for (const [k, v] of Object.entries(sourceMap)) workerHoursPerDayMap[k] = v
  } else {
    for (const row of response.rows) {
      const key = String(row.userProfileId)
      if (workerHoursPerDayMap[key] === undefined) workerHoursPerDayMap[key] = row.hoursPerDay
    }
  }

  const views: WorkerView[] = []

  for (const idStr of Object.keys(workerHoursPerDayMap)) {
    const userProfileId = Number(idStr)
    if (!Number.isFinite(userProfileId)) continue
    const hoursPerDay = workerHoursPerDayMap[idStr]
    const rows = byWorker.get(userProfileId) ?? []
    const rowByWeekStart = new Map(rows.map(r => [r.bucketStart.slice(0, 10), r]))

    // Weekly cells, one per ordered week (~52 over 12mo). Whole weeks → idle capacity is 5 days.
    const weeklyCells: WorkerMonthCell[] = orderedWeeks.map(weekStartIso =>
      buildCell(weekStartIso, rowByWeekStart.get(weekStartIso), hoursPerDay, 5,
        weekLabel(weekStartIso), isCurrentWeek(weekStartIso)))

    // Monthly cells straight from the BE's monthly row set (each month already holds only its
    // own in-window days). Fall back to weekly aggregation for an older BE that omits them.
    let monthlyCells: WorkerMonthCell[]
    if (hasMonthlyRows) {
      const rowByMonth = new Map((byWorkerMonthly.get(userProfileId) ?? []).map(r => [r.bucketStart.slice(0, 7), r]))
      monthlyCells = orderedMonths.map(monthStartIso =>
        buildCell(monthStartIso, rowByMonth.get(monthStartIso.slice(0, 7)), hoursPerDay,
          workingDaysByMonth.get(monthStartIso) ?? 0, monthLabel(monthStartIso), isCurrentMonth(monthStartIso)))
    } else {
      // Legacy fallback: aggregate weekly cells, attributing a leading week (Monday before the
      // window start) to the first window month so its in-window days aren't dropped.
      const firstMonthYM = orderedMonths[0]?.slice(0, 7)
      monthlyCells = orderedMonths.map(monthStartIso => {
        const monthYM = monthStartIso.slice(0, 7)
        const weeksInMonth = weeklyCells.filter(c => {
          const weekYM = c.monthStart.slice(0, 7)
          const attributedYM = firstMonthYM && weekYM < firstMonthYM ? firstMonthYM : weekYM
          return attributedYM === monthYM
        })
        return aggregateWeeksToMonth(monthStartIso, weeksInMonth)
      })
    }

    const profile = profiles.get(userProfileId)
    const name = profile?.workerName ?? `Worker ${userProfileId}`

    // Anchor "current" facts on the actual current-month cell rather than cells[0]. The
    // window almost always starts at the current month, but if the caller ever shifts
    // the start (or the current month falls outside the window), defaulting to 0/false
    // is safer than reading from an arbitrary other month.
    const currentCell = monthlyCells.find(c => c.isCurrentMonth)
    const activeEngagementCount = currentCell?.activeEngagementIds.length ?? 0

    // Engagements the worker is a CURRENT member of — powers the engagement filter (GW-153).
    // workerCurrentEngagementIds is the authoritative source (current workers, any BHPD,
    // excludes removed workers). Fall back to the spans/BHPD/cell union only for an older BE
    // that doesn't send it — that union includes removed workers and BHPD>0-gated cells.
    const engagementIdSet = new Set<string>()
    if (response.workerCurrentEngagementIds) {
      for (const eid of response.workerCurrentEngagementIds[idStr] ?? []) engagementIdSet.add(String(eid))
    } else {
      for (const eid of Object.keys(response.workerEngagementSpans?.[idStr] ?? {})) engagementIdSet.add(eid)
      for (const eid of Object.keys(response.workerEngagementBudgetedHoursPerDay?.[idStr] ?? {})) engagementIdSet.add(eid)
      for (const cell of monthlyCells) {
        for (const eid of cell.activeEngagementIds) engagementIdSet.add(eid)
      }
    }
    const engagementIds = Array.from(engagementIdSet).sort((a, b) => a.localeCompare(b))

    // Resolve manager display name from the same profiles map. Managers are pre-fetched
    // by the data hook and merged into peopleRaw, so internal-only managers (no engagement
    // workload of their own) still resolve. Unresolved managers leave functionalManagerName
    // undefined; the filter UI falls back to the raw ID.
    const managerId = profile?.functionalManagerProfileId
    const managerName = managerId !== undefined ? profiles.get(managerId)?.workerName : undefined

    views.push({
      userProfileId,
      workerName: name,
      workerInitials: workerInitials(name),
      workerRole: profile?.workerRole,
      jobFunctions: profile?.jobFunctions,
      skills: profile?.skills,
      certifications: profile?.certifications,
      branchCode: profile?.branchCode,
      workerLocation: profile?.workerLocation,
      procurementSourceCode: profile?.procurementSourceCode,
      functionalManagerProfileId: managerId,
      functionalManagerName: managerName,
      engagementIds,
      hoursPerDay,
      profileEndDate: response.workerProfileEndDates?.[idStr],
      months: monthlyCells,
      weeklyCells,
      hasOverallocatedMonth: monthlyCells.some(c => c.overallocated),
      activeEngagementCount,
    })
  }

  views.sort((a, b) => a.workerName.localeCompare(b.workerName))
  return views
}

/**
 * Turn one bucket row (weekly or monthly) into a heatmap cell, or synthesize an idle cell
 * when the sparse payload omitted the row. Shared by both granularities so the availability
 * math lives in exactly one place. `label`/`isCurrent` are supplied by the caller since they
 * differ per granularity; `fallbackWorkingDays` sizes the idle-cell capacity.
 */
function buildCell(
  bucketStartIso: string,
  row: ResourceAvailabilityRow | undefined,
  hoursPerDay: number,
  fallbackWorkingDays: number,
  label: string,
  isCurrent: boolean
): WorkerMonthCell {
  if (!row) {
    // Synthesize the cell the BE would have emitted for this idle bucket. Capacity uses the
    // worker's hoursPerDay × weekdays — getting this right matters because allocated% is
    // planned ÷ capacity (a zero-capacity bucket in the denominator would distort it).
    return {
      monthStart: bucketStartIso,
      monthLabel: label,
      isCurrentMonth: isCurrent,
      allocatedPercent: 0,
      availablePercent: 100,
      plannedHours: 0,
      plannedWorkingHours: 0,
      ptoHours: 0,
      statHours: 0,
      ptoDates: [],
      statHolidayDates: [],
      capacityHours: hoursPerDay * fallbackWorkingDays,
      overallocated: false,
      activeEngagementIds: [],
    }
  }

  const capacityHours = row.hoursPerDay * row.workingDaysInBucket
  // Allocation = BHPD coverage of nominal weekday capacity. Forecast-only: plan, not actuals.
  // PTO is ignored and stat holidays are included here (the backend counts BHPD on stat days in
  // plannedHours, and the denominator is full capacityHours) — keeping allocation PTO-blind
  // avoids false ">100% / action required" alarms for a fully-booked worker who takes leave.
  const allocPct = capacityHours > 0
    ? Math.round((row.plannedHours / capacityHours) * 100)
    : row.plannedHours > 0
      ? 100
      : 0
  // Available % is a standalone "how much genuinely free time is there?" metric (GW-230): it
  // deducts project work + PTO + stat holidays from capacity, so it is NOT the complement of
  // allocated (the two no longer sum to 100). Uses plannedWorkingHours — budget on genuinely
  // worked days — NOT plannedHours, because the backend counts BHPD on PTO/stat days too, so
  // subtracting plannedHours + PTO + stat would double-count that overlap. Can still go
  // negative when commitments exceed capacity — left unclamped so the heatmap renders
  // over-capacity cells red in Available mode.
  const plannedWorkingHours = row.plannedWorkingHours ?? row.plannedHours
  const availPct = capacityHours > 0
    ? Math.round(((capacityHours - plannedWorkingHours - row.ptoHours - row.statHolidayHours) / capacityHours) * 100)
    : 0
  const overallocated = row.maxDailyBudgetedHoursPerDay > row.hoursPerDay

  return {
    monthStart: bucketStartIso,
    monthLabel: label,
    isCurrentMonth: isCurrent,
    allocatedPercent: allocPct,
    availablePercent: availPct,
    plannedHours: row.plannedHours,
    plannedWorkingHours,
    ptoHours: row.ptoHours,
    statHours: row.statHolidayHours,
    ptoDates: row.ptoDates ?? [],
    statHolidayDates: row.statHolidayDates ?? [],
    capacityHours,
    overallocated,
    activeEngagementIds: parseEngagementIds(row.activeEngagementIdsCsv),
  }
}

/**
 * Fallback for an older BE that doesn't send `monthlyRows`: sum the weekly cells that fall in
 * this month into a single monthly cell. Allocated % recomputes from total planned ÷ total
 * capacity (NOT a mean of weekly percents — that would skew when weeks differ in length).
 * Max-daily takes the max across weeks. Engagement IDs union across weeks.
 */
function aggregateWeeksToMonth(monthStartIso: string, weekCells: WorkerMonthCell[]): WorkerMonthCell {
  if (weekCells.length === 0) {
    return {
      monthStart: monthStartIso,
      monthLabel: monthLabel(monthStartIso),
      isCurrentMonth: isCurrentMonth(monthStartIso),
      allocatedPercent: 0,
      availablePercent: 100,
      plannedHours: 0,
      plannedWorkingHours: 0,
      ptoHours: 0,
      statHours: 0,
      capacityHours: 0,
      overallocated: false,
      activeEngagementIds: [],
    }
  }

  let plannedHours = 0
  let plannedWorkingHours = 0
  let ptoHours = 0
  let statHours = 0
  let capacityHours = 0
  let overallocated = false
  const engagementIds = new Set<string>()
  // Weeks are attributed to a month by their Monday, so a week never spans two monthly
  // buckets — concatenating each week's dates yields a distinct, already-ordered list.
  const ptoDates: PtoDate[] = []
  const statHolidayDates: StatHolidayDate[] = []
  for (const c of weekCells) {
    plannedHours += c.plannedHours
    plannedWorkingHours += c.plannedWorkingHours
    ptoHours += c.ptoHours
    statHours += c.statHours
    capacityHours += c.capacityHours
    if (c.overallocated) overallocated = true
    for (const id of c.activeEngagementIds) engagementIds.add(id)
    if (c.ptoDates) ptoDates.push(...c.ptoDates)
    if (c.statHolidayDates) statHolidayDates.push(...c.statHolidayDates)
  }

  const allocPct = capacityHours > 0
    ? Math.round((plannedHours / capacityHours) * 100)
    : plannedHours > 0
      ? 100
      : 0
  // Standalone availability from summed totals (GW-230): capacity minus working-day project
  // hours, PTO and stat holidays. Uses plannedWorkingHours (not plannedHours) to avoid the
  // PTO/stat double-count. Recomputed from totals (not averaged from weekly cells).
  const availPct = capacityHours > 0
    ? Math.round(((capacityHours - plannedWorkingHours - ptoHours - statHours) / capacityHours) * 100)
    : 0

  return {
    monthStart: monthStartIso,
    monthLabel: monthLabel(monthStartIso),
    isCurrentMonth: isCurrentMonth(monthStartIso),
    allocatedPercent: allocPct,
    availablePercent: availPct,
    plannedHours,
    plannedWorkingHours,
    ptoHours,
    statHours,
    ptoDates,
    statHolidayDates,
    capacityHours,
    overallocated,
    activeEngagementIds: Array.from(engagementIds).sort((a, b) => a.localeCompare(b)),
  }
}

function parseEngagementIds(csv: string | null): string[] {
  if (!csv) return []
  // Trim each token so set-equality checks downstream aren't broken by stray whitespace
  // if the BE ever produces a CSV with spaces around separators.
  return csv.split(',').map(s => s.trim()).filter(Boolean)
}

function monthsInWindow(startMonthIso: string, endMonthIso: string): string[] {
  const result: string[] = []
  const cursor = new Date(startMonthIso)
  const end = new Date(endMonthIso)
  while (cursor <= end) {
    result.push(toIsoMonth(cursor))
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }
  return result
}

/**
 * Enumerate every Monday in the inclusive window. Includes the Monday before
 * `startMonthIso` if it isn't already a Monday, so partial leading weeks aren't lost.
 */
function weeksInWindow(startMonthIso: string, endMonthIso: string): string[] {
  const start = new Date(startMonthIso)
  // Walk backwards to the most recent Monday.
  const dow = start.getUTCDay() // Sun=0, Mon=1, ...
  const offset = (dow - 1 + 7) % 7
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() - offset))

  // End cap: include weeks up through the last day of `endMonthIso`'s month.
  const endMonthStart = new Date(endMonthIso)
  const endCap = new Date(Date.UTC(endMonthStart.getUTCFullYear(), endMonthStart.getUTCMonth() + 1, 0))

  const result: string[] = []
  while (cursor <= endCap) {
    result.push(toIsoDate(cursor))
    cursor.setUTCDate(cursor.getUTCDate() + 7)
  }
  return result
}

function toIsoMonth(d: Date): string {
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  return `${yyyy}-${mm}-01`
}

function toIsoDate(d: Date): string {
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function lastDayOfMonthIso(monthStartIso: string): string {
  const d = new Date(monthStartIso)
  return toIsoDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)))
}

/**
 * Count weekdays (Mon–Fri) of `monthStartIso`'s month that fall within `[windowStart,
 * windowEnd]`. Sizes the synthesized idle monthly cell's capacity so an idle worker's month
 * uses the same denominator the BE would have on a real row.
 */
function countWeekdaysInMonth(monthStartIso: string, windowStartIso: string, windowEndIso: string): number {
  const monthStart = new Date(monthStartIso)
  const monthEnd = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 0))
  const ws = new Date(windowStartIso)
  const we = new Date(windowEndIso)
  const from = monthStart < ws ? ws : monthStart
  const to = monthEnd > we ? we : monthEnd
  let count = 0
  for (const d = new Date(from); d <= to; d.setUTCDate(d.getUTCDate() + 1)) {
    const dow = d.getUTCDay() // Sun=0, Sat=6
    if (dow !== 0 && dow !== 6) count++
  }
  return count
}
